import React, { useMemo, useState } from 'react';
import { Alert, Form, Icon, Input } from 'antd';
import classNames from 'classnames';
import {
  formatPurchaseAmountWithCode,
  getPurchaseLineTotal,
  isValidPurchaseEmail,
  parsePurchaseQuantity,
} from '../purchaseHelpers';

const FormItem = Form.Item;

function PayPalCheckoutButton({ disabled, loading }) {
  return (
    <button type="submit" className="purchase-paypal-btn" disabled={disabled || loading}>
      {loading ? (
        <>
          <Icon type="loading" />
          <span>Redirecting to PayPal…</span>
        </>
      ) : (
        <span>Pay with PayPal</span>
      )}
    </button>
  );
}

export default function PurchaseCheckout({
  plan,
  defaultEmail,
  defaultQuantity,
  onBack,
  onSubmit,
  isSubmitting,
  errorMessage,
}) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [quantityInput, setQuantityInput] = useState(String(defaultQuantity || 1));
  const [fieldErrors, setFieldErrors] = useState({});

  const quantity = useMemo(() => parsePurchaseQuantity(quantityInput) || 0, [quantityInput]);
  const lineTotal = useMemo(() => (quantity > 0 ? getPurchaseLineTotal(plan, quantity) : 0), [plan, quantity]);

  const validate = () => {
    const nextErrors = {};
    if (!isValidPurchaseEmail(email)) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (!parsePurchaseQuantity(quantityInput)) {
      nextErrors.quantity = 'Quantity must be at least 1.';
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSubmitting || !validate()) return;
    onSubmit({ email: email.trim(), quantity: parsePurchaseQuantity(quantityInput) });
  };

  return (
    <div className="purchase-checkout">
      <button type="button" className="purchase-back-link" onClick={onBack}>
        <span aria-hidden="true">←</span> Back to plans
      </button>

      <div className="purchase-checkout-grid">
        <section className="purchase-order-summary" aria-label="Order summary">
          <p className="purchase-panel-label">Order summary</p>

          <div className="purchase-order-plan">
            {plan.badge?.label ? (
              <span className={classNames('purchase-plan-badge', `purchase-plan-badge--${plan.badge.tone}`)}>
                {plan.badge.label}
              </span>
            ) : null}
            <h3 className="purchase-order-plan-name">{plan.name}</h3>
            <p className="purchase-order-plan-description">{plan.description}</p>
          </div>

          <div className="purchase-order-line">
            <span>Price per plan</span>
            <span>{formatPurchaseAmountWithCode(plan.price, plan.currencyCode, plan.currencySymbol)}</span>
          </div>
          <div className="purchase-order-line">
            <span>Quantity</span>
            <span>{quantity > 0 ? quantity : '—'}</span>
          </div>

          <div className="purchase-order-total">
            <span>Total due today</span>
            <strong>
              {quantity > 0 ? formatPurchaseAmountWithCode(lineTotal, plan.currencyCode, plan.currencySymbol) : '—'}
            </strong>
          </div>

          <p className="purchase-order-secure">One-time payment · No subscription · Secured via PayPal</p>
        </section>

        <section className="purchase-pay-panel" aria-label="Pay with PayPal">
          <h2 className="purchase-pay-title">Pay with PayPal</h2>
          <p className="purchase-pay-note">
            You&apos;ll approve this payment on PayPal&apos;s site. Card and account details never touch this page.
          </p>

          <Form layout="vertical" className="purchase-checkout-form" onSubmit={handleSubmit}>
            <FormItem
              label="Email address"
              extra="Subscription details will be sent to this address."
              validateStatus={fieldErrors.email ? 'error' : ''}
              help={fieldErrors.email}
            >
              <Input
                id="purchase-email"
                type="email"
                placeholder="you@company.com"
                value={email}
                disabled={isSubmitting}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }));
                }}
              />
            </FormItem>

            <FormItem
              label="Quantity"
              validateStatus={fieldErrors.quantity ? 'error' : ''}
              help={fieldErrors.quantity}
            >
              <Input
                id="purchase-quantity"
                type="number"
                min={1}
                value={quantityInput}
                disabled={isSubmitting}
                onChange={(event) => {
                  setQuantityInput(event.target.value);
                  if (fieldErrors.quantity) setFieldErrors((prev) => ({ ...prev, quantity: undefined }));
                }}
              />
            </FormItem>

            {errorMessage ? (
              <Alert type="error" showIcon message={errorMessage} className="purchase-checkout-alert" />
            ) : null}

            <PayPalCheckoutButton disabled={quantity < 1} loading={isSubmitting} />
          </Form>
        </section>
      </div>
    </div>
  );
}
